import { useState } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { LayoutDashboard, Package, Plus, Search, User, LogOut, Menu, X, ChevronDown, Settings } from 'lucide-react';
import { Button } from '@/components/ui/button';
import BrandLogo from '@/components/BrandLogo';

const navItems = [
  { to: '/client', icon: LayoutDashboard, label: 'Dashboard', end: true },
  { to: '/client/shipments', icon: Package, label: 'Shipments' },
  { to: '/client/new-shipment', icon: Plus, label: 'New Shipment' },
  { to: '/client/history', icon: Search, label: 'History' },
  { to: '/client/profile', icon: User, label: 'Profile' },
];

export default function ClientLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const [userMenu, setUserMenu] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-background text-foreground flex" data-testid="client-layout">
      {/* Sidebar */}
      <aside className={`${collapsed ? 'w-16' : 'w-56'} bg-sidebar border-r border-sidebar-border flex flex-col transition-all duration-200 shrink-0`}>
        <div className="p-4 flex items-center justify-between">
          {!collapsed && (
            <div className="flex items-center gap-2">
              <BrandLogo size="sm" />
              <span className="text-[10px] text-muted-foreground">Shipper</span>
            </div>
          )}
          <Button variant="ghost" size="icon" onClick={() => setCollapsed(!collapsed)} className="text-muted-foreground hover:text-foreground h-8 w-8">
            {collapsed ? <Menu className="w-4 h-4" /> : <X className="w-4 h-4" />}
          </Button>
        </div>

        <nav className="flex-1 py-4 space-y-1 px-2 overflow-y-auto">
          {navItems.map(item => (
            <NavLink key={item.to} to={item.to} end={item.end}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors ${
                  isActive ? 'bg-sidebar-active text-sidebar-active-foreground border border-brand-accent/20' : 'text-muted-foreground hover:text-foreground hover:bg-muted/50 border border-transparent'
                }`
              }>
              <item.icon className="w-4 h-4 shrink-0" strokeWidth={1.5} />
              {!collapsed && <span>{item.label}</span>}
            </NavLink>
          ))}
        </nav>

        <div className="p-3 border-t border-sidebar-border">
          <Button variant="ghost" onClick={handleLogout}
            className="w-full flex items-center gap-2 text-muted-foreground hover:text-red-400 justify-start text-sm">
            <LogOut className="w-4 h-4" strokeWidth={1.5} />
            {!collapsed && <span>Sign Out</span>}
          </Button>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col overflow-hidden">
        <header className="bg-sidebar border-b border-sidebar-border px-6 py-3 flex items-center justify-end gap-3 shrink-0">
          <div className="relative">
            <button onClick={() => setUserMenu(!userMenu)}
              className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-muted transition-colors">
              <div className="w-8 h-8 rounded-full bg-brand-accent/20 flex items-center justify-center text-sm font-bold text-brand-accent">
                {user?.name?.charAt(0)?.toUpperCase() || 'S'}
              </div>
              <span className="text-sm text-muted-foreground">{user?.name}</span>
              <ChevronDown className="w-3 h-3 text-muted-foreground" />
            </button>
            {userMenu && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setUserMenu(false)} />
                <div className="absolute right-0 top-full mt-1 w-44 bg-card border border-border rounded-lg shadow-xl z-50 py-1">
                  <button onClick={() => { setUserMenu(false); navigate('/client/profile'); }}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-muted-foreground hover:text-foreground hover:bg-muted">
                    <Settings className="w-3.5 h-3.5" /> Profile
                  </button>
                  <div className="border-t border-border my-1" />
                  <button onClick={() => { setUserMenu(false); handleLogout(); }}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-muted-foreground hover:text-red-400 hover:bg-muted">
                    <LogOut className="w-3.5 h-3.5" /> Sign Out
                  </button>
                </div>
              </>
            )}
          </div>
        </header>

        <main className="flex-1 overflow-auto">
          <div className="p-6">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
